// One project: title, then planning and estimate as two tabs, and on the
// estimate the approve bar.
// Functional level of the toolkit: entrances and state changes only. Each part
// comes in once as it reaches the screen (not every time, as on the feed):
// this is read closely, often more than once.
import * as React from "react"
import { Dots } from "../toolkit/Dots"
import { EASE, MS, enter, leave, linesIn, reduced, stagger } from "../toolkit/motion"
import { approve, day, fmtDate, fmtMoney, type Entry } from "./api"
import { Link, type Route, type Tab } from "./route"
import { SaveImageButton } from "./SaveImage"
import type { Approval } from "../lib/auth"
import { locale, t, unit } from "./i18n"
const fmtNum = (n: number) => new Intl.NumberFormat(locale()).format(n)

/** parts marked data-r come in as they reach the screen, in order */
function useReveal(root: React.RefObject<HTMLElement>, delay = 0) {
    React.useEffect(() => {
        const el = root.current
        if (!el) return
        const groups = Array.from(el.querySelectorAll<HTMLElement>("[data-reveal]"))
        for (const g of groups) g.style.visibility = "hidden"
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (!e.isIntersecting) continue
                    io.unobserve(e.target)
                    const g = e.target as HTMLElement
                    g.style.visibility = ""
                    const first = g.dataset.first === "1"
                    stagger(g.querySelectorAll("[data-r]"), { from: "up", distance: 28, gap: MS.stagger, delay: first ? delay : 0 })
                    for (const t of g.querySelectorAll<HTMLElement>("[data-lines]")) linesIn(t, { delay: first ? delay : 0 })
                    for (const [i, l] of Array.from(g.querySelectorAll<HTMLElement>("[data-draw]")).entries()) {
                        l.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
                            duration: reduced() ? 0 : MS.fill,
                            delay: (first ? delay : 0) + 200 + i * MS.stagger,
                            easing: EASE.natural,
                            fill: "backwards",
                        })
                    }
                }
            },
            { threshold: 0.08 }
        )
        for (const g of groups) io.observe(g)
        return () => io.disconnect()
    }, [])
}

export function ProjectPage(props: { client: string; clientName: string; logo: string | null; entry: Entry; route: Route; fromLogin: boolean; onReload: () => void }) {
    const { client, entry, route, fromLogin, onReload } = props
    const root = React.useRef<HTMLElement>(null)
    const { project: p, totals } = entry
    const [approval, setApproval] = React.useState<Approval | null>(entry.approval)
    React.useEffect(() => setApproval(entry.approval), [entry])
    const cur = p.estimate.currency
    const delay = fromLogin ? 80 : 120
    useReveal(root, delay)

    // the shown tab: on a switch, the current one leaves the way the
    // indicator moves, then the other comes in from that side
    const [shown, setShown] = React.useState<Tab>(route.tab)
    const [dir, setDir] = React.useState<"left" | "right" | null>(null)
    const panel = React.useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        if (route.tab === shown) return
        const d = route.tab === "estimate" ? "left" : "right"
        const parts = Array.from(panel.current?.querySelectorAll<HTMLElement>("[data-r]") || []).filter((el) => {
            const r = el.getBoundingClientRect()
            return r.bottom > 0 && r.top < innerHeight
        })
        let alive = true
        leave(parts.slice(0, 12), { to: d, gap: 22, distance: 24 }).then(() => {
            if (!alive) return
            const tabs = document.querySelector(".t-tabs-wrap") as HTMLElement | null
            if (tabs && tabs.getBoundingClientRect().top < 0) window.scrollTo({ top: tabs.offsetTop - 20 })
            setDir(d)
            setShown(route.tab)
        })
        return () => {
            alive = false
        }
    }, [route.tab])

    return (
        <main ref={root} className="p-page">
            <section className="p-hero" data-reveal data-first="1">
                <Link className="p-back nto-mono" to={{ client, tab: "planning" }} data-r>
                    <Dots shape="arrowLeft" />
                    <span>{t("All projects")}</span>
                </Link>
                <div className="p-meta nto-mono" data-r>
                    <span>{p.client}</span>
                    <span>{p.reference}</span>
                    <span>{fmtDate(p.date, true)}</span>
                </div>
                <h1 className="p-title" data-lines>
                    {p.title}
                </h1>
                {p.intro && (
                    <p className="p-intro" data-r>
                        {p.intro}
                    </p>
                )}
            </section>

            <Tabs route={route} approved={!!approval} delay={delay + 250}>
                <SaveImageButton project={p} kind={route.tab} clientName={props.clientName} logo={props.logo} approval={approval} />
            </Tabs>

            <div ref={panel} key={shown} className="t-panel">
                {shown === "planning" ? (
                    <Planning entry={entry} dir={dir} />
                ) : (
                    <Estimate entry={entry} approval={approval} dir={dir} />
                )}
            </div>

            {shown === "estimate" && (
                <ApproveBar
                    client={client}
                    project={p.id}
                    version={entry.version}
                    total={fmtMoney(totals.total, cur)}
                    approval={approval}
                    onApproved={setApproval}
                    onChanged={onReload}
                    delay={dir ? 300 : fromLogin ? 900 : 1000}
                />
            )}
        </main>
    )
}

/** a switched-to tab's parts come in from the side they were pushed to */
function useTabIn(ref: React.RefObject<HTMLElement>, dir: "left" | "right" | null) {
    React.useLayoutEffect(() => {
        if (!dir || !ref.current) return
        ref.current.style.visibility = ""
        stagger(ref.current.querySelectorAll("[data-r]"), { from: dir === "left" ? "right" : "left", distance: 24, gap: 40, duration: MS.enter })
        ref.current.querySelectorAll<HTMLElement>("[data-draw]").forEach((l, i) =>
            l.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
                duration: reduced() ? 0 : MS.fill,
                delay: 150 + i * MS.stagger,
                easing: EASE.natural,
                fill: "backwards",
            })
        )
    }, [])
}

// ---- tabs: one black pill, a white one sliding inside it ----
function Tabs({ route, approved, delay, children }: { route: Route; approved: boolean; delay: number; children?: React.ReactNode }) {
    const wrap = React.useRef<HTMLDivElement>(null)
    React.useLayoutEffect(() => {
        if (wrap.current) enter(wrap.current, { from: "up", distance: 20, delay, duration: MS.enter })
    }, [])
    // the white pill sits under the current tab, and slides when it changes
    const first = React.useRef(true)
    React.useLayoutEffect(() => {
        const nav = wrap.current?.querySelector<HTMLElement>(".t-tabs")
        const a = nav?.querySelector<HTMLElement>("[aria-current]")
        const ink = nav?.querySelector<HTMLElement>(".t-ink")
        if (!a || !ink) return
        const place = () => {
            ink.style.width = a.offsetWidth + "px"
            ink.style.transform = `translateX(${a.offsetLeft}px)`
        }
        if (first.current) {
            ink.style.transition = "none"
            place()
            void ink.offsetWidth
            ink.style.transition = ""
            first.current = false
            document.fonts?.ready.then(() => {
                ink.style.transition = "none"
                place()
                void ink.offsetWidth
                ink.style.transition = ""
            })
        } else place()
    }, [route.tab])
    return (
        <div ref={wrap} className="t-tabs-wrap">
            <nav className="t-tabs nto-label" data-tab={route.tab}>
                <span className="t-ink" aria-hidden="true" />
                <Link to={{ ...route, tab: "planning" }} aria-current={route.tab === "planning" ? "page" : undefined}>
                    {t("Planning")}
                </Link>
                <Link to={{ ...route, tab: "estimate" }} aria-current={route.tab === "estimate" ? "page" : undefined}>
                    {t("Estimate")}
                    {!approved && <i className="t-flag" aria-label="to approve" />}
                </Link>
            </nav>
            {children}
        </div>
    )
}

// ---- estimate ----
function Estimate({ entry, approval, dir }: { entry: Entry; approval: Approval | null; dir: "left" | "right" | null }) {
    const { project: p, totals } = entry
    const cur = p.estimate.currency
    const ref = React.useRef<HTMLElement>(null)
    useTabIn(ref, dir)
    return (
        <section ref={ref} className="p-section" data-reveal={dir ? undefined : ""}>
            <h2 className="p-section-label nto-label" data-r>
                {t("Estimate")}
            </h2>
            <div className="p-estimate">
                {p.estimate.groups.map((g) => (
                    <div className="p-group" key={g.name}>
                        <h3 className="p-group-name nto-label" data-r>
                            {g.name}
                        </h3>
                        {g.lines.map((l, i) => (
                            <div className="p-line" key={i} data-r>
                                <div className="p-line-item">
                                    <span>{l.item}</span>
                                    {l.note && <span className="p-line-note">{l.note}</span>}
                                </div>
                                <span className="p-line-qty nto-mono">{l.unit === "fixed" ? unit("fixed") : `${fmtNum(l.qty)} ${unit(l.unit)}`}</span>
                                <span className="p-line-rate nto-mono">{l.unit === "fixed" ? "" : `× ${fmtMoney(l.rate, cur, false)}`}</span>
                                <span className="p-line-amount nto-mono">{fmtMoney(l.qty * l.rate, cur)}</span>
                            </div>
                        ))}
                    </div>
                ))}
                <div className="p-sums" data-r>
                    <div className="p-sum nto-mono">
                        <span>{t("Subtotal")}</span>
                        <span>{fmtMoney(totals.net, cur)}</span>
                    </div>
                    <div className="p-sum nto-mono">
                        <span>{t("VAT")} {p.estimate.vat}%</span>
                        <span>{fmtMoney(totals.vat, cur)}</span>
                    </div>
                    <div className="p-total">
                        <span className="nto-label">{t("Total")}</span>
                        <span className="p-total-amount">{fmtMoney(totals.total, cur)}</span>
                    </div>
                </div>
                <div className="p-notes nto-mono" data-r>
                    {approval ? (
                        <p className="p-approved-note">{t("Approved on")} {fmtDate(approval.at.slice(0, 10), true)}</p>
                    ) : (
                        <p>{t("Valid until")} {fmtDate(p.estimate.validUntil, true)}</p>
                    )}
                    {p.estimate.notes?.map((n, i) => (
                        <p key={i}>{n}</p>
                    ))}
                </div>
            </div>
        </section>
    )
}

// ---- planning: the section indicator's language as a timeline ----
// One dot per phase, its line as long as the phase on a shared time axis,
// filled as far as the project has got. Today is a hairline with a dot.

export function Planning({ entry, dir }: { entry: Entry; dir: "left" | "right" | null }) {
    const { phases, milestones = [] } = entry.project.planning
    const ref = React.useRef<HTMLElement>(null)
    useTabIn(ref, dir)
    const start = Math.min(...phases.map((p) => day(p.start)))
    const end = Math.max(...phases.map((p) => day(p.end)))
    const span = Math.max(1, end - start)
    const at = (t: number) => Math.min(100, Math.max(0, ((t - start) / span) * 100))
    const now = Date.now()
    const months: { label: string; x: number }[] = []
    const m = new Date(start)
    m.setDate(1)
    m.setMonth(m.getMonth() + 1)
    while (m.getTime() <= end) {
        months.push({ label: new Intl.DateTimeFormat(locale(), { month: "short" }).format(m), x: at(m.getTime()) })
        m.setMonth(m.getMonth() + 1)
    }
    const showToday = now > start && now < end

    return (
        <section ref={ref} className="p-section" data-reveal={dir ? undefined : ""}>
            <h2 className="p-section-label nto-label" data-r>
                {t("Planning")}
            </h2>
            <div className="p-plan">
                <div className="p-axis nto-mono" data-r aria-hidden="true">
                    <span style={{ left: 0 }}>{fmtDate(phases[0].start)}</span>
                    {months.map((mo) => (
                        <span key={mo.label + mo.x} style={{ left: `${mo.x}%` }} className="p-axis-month">
                            {mo.label}
                        </span>
                    ))}
                </div>
                <div className="p-phases">
                    {showToday && (
                        <div className="p-today-lane" aria-hidden="true">
                            <div className="p-today" style={{ left: `${at(now)}%` }}>
                                <i />
                                <span className="nto-mono">{t("Today")}</span>
                            </div>
                        </div>
                    )}
                    {phases.map((ph, i) => {
                        const s = day(ph.start),
                            e = day(ph.end) + 86400000 * 0.5
                        const done = Math.min(1, Math.max(0, (now - s) / (e - s)))
                        const state = done >= 1 ? "done" : done > 0 ? "now" : "next"
                        return (
                            <div className="p-phase" key={i} data-state={state} data-r>
                                <div className="p-phase-text">
                                    <span className="p-phase-name">{ph.name}</span>
                                    <span className="p-phase-dates nto-mono">
                                        {fmtDate(ph.start)} – {fmtDate(ph.end)}
                                    </span>
                                    {ph.note && <span className="p-phase-note">{ph.note}</span>}
                                </div>
                                <div className="p-track">
                                    <div className="p-run" style={{ left: `${at(s)}%`, width: `${Math.max(0.6, at(e) - at(s))}%` }}>
                                        <i className="p-run-dot" />
                                        <span className="p-run-line" data-draw>
                                            <span className="p-run-fill" style={{ transform: `scaleX(${done})` }} />
                                        </span>
                                        <i className="p-run-dot p-run-end" />
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
                {milestones.length > 0 && (
                    <div className="p-milestones nto-mono" data-r>
                        {milestones.map((ms) => (
                            <span key={ms.name}>
                                <Dots shape="dot" />
                                {ms.name} · {fmtDate(ms.date)}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}

// ---- approve: a black bar like the contact form ----

type Step = "idle" | "confirm" | "sending" | "error"

function ApproveBar(props: {
    client: string
    project: string
    version: string
    total: string
    approval: Approval | null
    onApproved: (a: Approval) => void
    onChanged: () => void
    delay: number
}) {
    const { client, project, version, total, approval, onApproved, onChanged, delay } = props
    const bar = React.useRef<HTMLDivElement>(null)
    const [step, setStep] = React.useState<Step>("idle")

    React.useLayoutEffect(() => {
        if (bar.current) enter(bar.current, { from: "up", distance: 40, delay, duration: MS.drift })
    }, [])

    // each change of step: the text comes in again, right to left
    const first = React.useRef(true)
    React.useLayoutEffect(() => {
        if (first.current) {
            first.current = false
            return
        }
        const parts = bar.current?.querySelectorAll("[data-in]")
        if (parts) stagger(Array.from(parts).reverse(), { from: "left", distance: 14, duration: 500, easing: EASE.out, gap: 50 })
    }, [step, !!approval])

    const go = async () => {
        setStep("sending")
        const r = await approve(client, project, version)
        if (r === "changed") {
            setStep("idle")
            onChanged()
            return
        }
        if (r === "error") return setStep("error")
        onApproved(r)
    }

    if (approval)
        return (
            <div ref={bar} className="p-approve" data-done>
                <span data-in>{t("Approved on {date}. Thank you.", { date: fmtDate(approval.at.slice(0, 10), true) })}</span>
                <Dots shape="cross" style={{ transform: "rotate(45deg) scale(.8)" }} />
            </div>
        )

    // (before asking, the whole bar is the button)
    if (step === "idle")
        return (
            <button ref={bar as any} type="button" className="p-approve p-bar-button" data-step="idle" onClick={() => setStep("confirm")}>
                <span data-in>{t("Approve estimate")}</span>
                <span className="p-approve-sum" data-in>
                    {total} {t("incl. VAT")}
                </span>
                <span className="p-send" aria-hidden="true" data-in>
                    <Dots shape="arrow" />
                </span>
            </button>
        )

    return (
        <div ref={bar} className="p-approve" data-step={step}>
            {(step === "confirm" || step === "sending" || step === "error") && (
                <>
                    <span data-in className={step === "error" ? "p-bad" : undefined}>
                        {step === "error" ? t("Not sent, try again?") : t("Approve {total}? This tells Tiago to go ahead.", { total })}
                    </span>
                    <button className="p-approve-cancel" onClick={() => setStep("idle")} data-in disabled={step === "sending"}>
                        {t("Not yet")}
                    </button>
                    <button className="p-send p-send-go" onClick={go} aria-label="Yes, approve" data-in disabled={step === "sending"}>
                        <span>{t("Yes")}</span>
                        <Dots shape="arrow" />
                    </button>
                </>
            )}
        </div>
    )
}
